import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { showSuccessToast, showErrorToast } from "../../components/CustomComponents/toast/toast";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { appSettings } from "../../app/appSettingsSlice";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import { formatCompanyDateTimeForApi } from "../../utils/companyDateTime";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import "./../order/ManageOrderFilters.css";
import "./salesByProductsReport.css";

const SalesByProducts = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const user = useSelector((state) => state.auth.user);
  const { profileData } = useSelector((state) => state.auth);
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const { getCompCurrency, getCompTimeZone, getCompUTCTimeZone } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const companyTzOptions = React.useMemo(
    () => ({ compTimeZone: getCompTimeZone(), compUTCTimeZone: getCompUTCTimeZone() }),
    [getCompTimeZone, getCompUTCTimeZone]
  );
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Debounce timer ref for search
  const searchTimeoutRef = useRef(null);
  const fetchDebounceRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const skipFilterFetchOnceRef = useRef(false);

  // Date range state - now handled by Redux

  // Reporting categories state
  const [reportingCategories, setReportingCategories] = useState([]);
  const [loadingReportingCategories, setLoadingReportingCategories] = useState(false);

  // Additional dropdown states
  const [locations, setLocations] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuGroups, setMenuGroups] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingMenuGroups, setLoadingMenuGroups] = useState(false);

  // Column selection and preferences
  const pageKey = "salesBreakdownByProduct";
  const defaultVisibleColumns = {
    productSKU: false, // Hidden by default
    quantitySold: true,
    grossSales: true,
    discountsPromotions: true,
    netSalesIncTax: true,
    netSalesExcTax: true,
    taxAmount: true,
    costOfGoods: true,
    grossProfit: true,
    grossProfitMargin: true,
    transactionQty: false, // Hidden by default
    refundQty: true,
    refundValue: true
  };

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "productName", label: "Product Name" };
  
  // Default filter: "All Locations" so reports show all branches unless user selects one
  const parentIdForLocation = CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultStaff = { value: "all", label: "All Staff" };
  const defaultDevice = { value: "all", label: "All Devices" };
  const defaultReportingCategory = { value: "all", label: "All Category Types" };
  const defaultCategory = { value: "all", label: "All Reporting Categories" };
  const defaultMenuGroup = { value: "all", label: "All Menus" };
  const defaultProductType = { value: "all", label: "All Products" };
  const defaultVariantViewMode = "combined";
  
  // Default to "This Week" if no existing filter
  const defaultDateRange = [
    moment().startOf('week').toDate(),
    moment().endOf('week').toDate(),
  ];
  
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  
  // Get filter values from Redux
  const location = preferences.location || defaultLocation;
  const staff = preferences.staff || defaultStaff;
  const device = preferences.device || defaultDevice;
  const reportingCategory = preferences.reportingCategory || defaultReportingCategory;
  const category = preferences.category || defaultCategory;
  const menuGroup = preferences.menuGroup || defaultMenuGroup;
  const productType = preferences.productType || defaultProductType;
  const variantViewMode = preferences.variantViewMode || defaultVariantViewMode;

  // Coerce cleared/null dates back to default "This Week"
  const dateRange = (() => {
    const stored = preferences.dateRange;
    if (!Array.isArray(stored)) return defaultDateRange;
    const startOk = stored[0] != null && moment(stored[0]).isValid();
    const endOk = stored[1] != null && moment(stored[1]).isValid();
    if (!startOk && !endOk) return defaultDateRange;
    return [
      startOk ? stored[0] : defaultDateRange[0],
      endOk ? stored[1] : defaultDateRange[1],
    ];
  })();
  
  // Get raw dates from Redux
  const [rawStartDate, rawEndDate] = dateRange;
  
  const dispatch = useDispatch();

  // Remove old filter state - now using Redux

  // Get app settings from Redux
  // const appSettings = useSelector((state) => state.appSettings);
  // const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Currency symbols mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };
  
  // Filter options: "All Locations" first, then parent + branches
  const allLocationsOption = { value: "all", label: "All Locations" };
  const locationOptions =
    locations.length > 0
      ? [allLocationsOption, ...locations]
      : parentIdForLocation != null && !isNaN(parentIdForLocation)
        ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
        : [allLocationsOption];

  const staffOptions = staffData.length > 0 ? staffData : [
    { value: "all", label: "All Staff" }
  ];

  const deviceOptions = devices.length > 0 ? devices : [
    { value: "all", label: "All Devices" }
  ];

  // Category Types options will be populated from API
  const reportingCategoryOptions = reportingCategories.length > 0 ? reportingCategories : [
    { value: "all", label: "All Category Types" }
  ];

  // Categories options will be populated from API
  const categoryOptions = categories.length > 0 ? categories : [
    { value: "all", label: "All Reporting Categories" }
  ];

  // Menus options will be populated from API
  const menuGroupOptions = menuGroups.length > 0 ? menuGroups : [
    { value: "all", label: "All Menus" }
  ];

  // Product type options (aligned with Manage Items selling / non-selling flags)
  const productTypeOptions = [
    { value: "all", label: "All Products" },
    { value: "SellingItems", label: "Selling Products" },
    { value: "NonSellingItems", label: "Non Selling Products" },
    { value: "MODIFIER", label: "Modifiers" },
    { value: "COMBO", label: "Combos" },
  ];

  // Sort options
  const sortByOptions = [
    { value: "productName", label: "Product Name" },
    { value: "quantitySold", label: "Quantity Sold" },
    { value: "grossSales", label: "Gross Sales" },
    { value: "netSalesIncTax", label: "Net Sales Inc. Tax" },
    { value: "grossProfit", label: "Gross Profit" }
  ];

  const sortOrderOptions = [
    { value: "ASC", label: "ASC" },
    { value: "DESC", label: "DESC" }
  ];

  // Sample data for demonstration
  const sampleData = [];

  // Handle filter changes with Redux
  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
    setPageNumber(1);
  };

  // Reset filters with Redux
  const resetFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    dispatch(setFilterField({ pageKey, field: "reportingCategory", value: defaultReportingCategory }));
    dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
    dispatch(setFilterField({ pageKey, field: "menuGroup", value: defaultMenuGroup }));
    dispatch(setFilterField({ pageKey, field: "productType", value: defaultProductType }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    dispatch(setFilterField({ pageKey, field: "variantViewMode", value: defaultVariantViewMode }));
    setSearch("");
  };

  // Clear all filters with automatic filtering
  const clearAllFilters = () => {
    resetFilters();
    setPageNumber(1);
  };

  // Handle search with automatic filtering
  const handleSearch = () => {
    setPageNumber(1);
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    fetchSalesData();
  };

  // Handle search input change with proper debouncing
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPageNumber(1);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      fetchSalesData();
    }, 500);
  };

  // Fetch sales data from API (optionalVariantViewMode avoids stale closure when toggling view)
  const fetchSalesData = useCallback(async (optionalVariantViewMode) => {
    const activeVariantViewMode = optionalVariantViewMode ?? variantViewMode;

    fetchAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;
    const requestSeq = ++fetchSeqRef.current;

    setLoading(true);
    try {
      // Convert dates if they are strings; fall back to page default range (This Week)
      const startDate = rawStartDate ? (typeof rawStartDate === 'string' ? new Date(rawStartDate) : rawStartDate) : null;
      const endDate = rawEndDate ? (typeof rawEndDate === 'string' ? new Date(rawEndDate) : rawEndDate) : null;
      const defaultStartDate = startDate || defaultDateRange[0];
      const defaultEndDate = endDate || defaultDateRange[1];
      
      const formattedStartDate = formatCompanyDateTimeForApi(defaultStartDate, companyTzOptions);
      const formattedEndDate = formatCompanyDateTimeForApi(defaultEndDate, companyTzOptions);
      
      const params = {
        CompID,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        locationFilter: isMain && location.value !== 'all' && location.value != null ? (typeof location.value === 'number' ? location.value : parseInt(location.value)) : null,
        staffFilter: staff.value !== 'all' ? staff.value : null,
        deviceFilter: device.value !== 'all' ? device.value : null,
        reportingCategoryFilter: reportingCategory.value !== 'all' ? (typeof reportingCategory.value === 'number' ? reportingCategory.value : parseInt(reportingCategory.value)) : null,
        categoryFilter: category.value !== 'all' ? (typeof category.value === 'number' ? category.value : parseInt(category.value)) : null,
        menuFilter: menuGroup.value !== 'all' ? (typeof menuGroup.value === 'number' ? menuGroup.value : parseInt(menuGroup.value)) : null,
        productTypeFilter: productType.value !== 'all' ? productType.value : null,
        searchTerm: search || null,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
        pageNumber: pageNumber,
        pageSize: pageSize.value,
        groupByVariant: activeVariantViewMode === "variant" ? 1 : 0
      };
      

      const response = await axiosInstance.get(APIs.GET_SALES_BREAKDOWN_BY_PRODUCT, {
        params,
        signal: abortController.signal,
        timeout: 120000,
      });

      if (requestSeq !== fetchSeqRef.current) return;

      if (response.data.status) {
        const responseData = response.data.data;
        
        // Handle different response structures
        let salesDataRaw = responseData?.SalesData || responseData?.salesData || responseData || [];
        const totalCount = responseData?.TotalCount || responseData?.totalCount || 0;
        const totalPages = responseData?.TotalPages || responseData?.totalPages || 0;
        
        // Normalize property names to camelCase (in case backend sends PascalCase)
        // Prefer API ProductName (full line built by report SP); then default title / name
        const getDisplayProductName = (it) => {
          const productName = (it.productName ?? it.ProductName ?? '').toString().trim();
          if (productName) return productName;
          const defaultVariantTitle = (it.menuItemDefaultVariantTitle ?? it.MenuItem_DefaultVariantTitle ?? '').toString().trim();
          const menuItemName = (it.menuItemName ?? it.MenuItem_Name ?? '').toString().trim();
          return defaultVariantTitle || menuItemName;
        };
        const salesData = Array.isArray(salesDataRaw) ? salesDataRaw.map(item => ({
          menuItemID: item.menuItemID || item.MenuItem_ID || item.menuItem_ID,
          productName: getDisplayProductName(item),
          variantName: item.variantName || item.VariantName || '', // Just the variant name (for reference)
          productSKU: item.productSKU || item.ProductSKU || '',
          productType: item.productType || item.ProductType || '',
          quantitySold: item.quantitySold || item.QuantitySold || 0,
          grossSales: item.grossSales || item.GrossSales || 0,
          discountsPromotions: item.discountsPromotions || item.DiscountsPromotions || 0,
          netSalesIncTax: item.netSalesIncTax || item.NetSalesIncTax || 0,
          netSalesExcTax: item.netSalesExcTax || item.NetSalesExcTax || 0,
          taxAmount: item.taxAmount || item.TaxAmount || 0,
          costOfGoods: item.costOfGoods || item.CostOfGoods || 0,
          grossProfit: item.grossProfit || item.GrossProfit || 0,
          grossProfitMargin: item.grossProfitMargin || item.GrossProfitMargin || 0,
          transactionQty: item.transactionQty || item.TransactionQty || 0,
          refundQty: item.refundQty || item.RefundQty || 0,
          refundValue: item.refundValue || item.RefundValue || 0,
          avgItemPrice: item.avgItemPrice || item.AvgItemPrice || 0,
          categoryName: item.categoryName || item.CategoryName || '',
          menuGroupName: item.menuGroupName || item.MenuGroupName || ''
        })) : [];
        
        setSalesData(salesData);
        setTotalRecords(totalCount);
        setTotalPages(totalPages);
      } else {
        showErrorToast(response.data.message || "Error loading sales data");
        setSalesData([]);
        setTotalRecords(0);
        setTotalPages(0);
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        return;
      }
      if (requestSeq !== fetchSeqRef.current) return;
      console.error('Error fetching sales data:', error);
      showErrorToast(error.response?.data?.message || error.message || "Error loading sales data");
      setSalesData([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      if (requestSeq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [CompID, rawStartDate, rawEndDate, location, staff, device, reportingCategory, category, menuGroup, productType, search, sortBy, sortOrder, pageNumber, pageSize, variantViewMode, companyTzOptions]);

  const scheduleFetchSalesData = useCallback(
    (optionalVariantViewMode, delayMs = 350) => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }
      fetchDebounceRef.current = setTimeout(() => {
        fetchSalesData(optionalVariantViewMode);
      }, delayMs);
    },
    [fetchSalesData]
  );

  const handleVariantViewModeChange = (mode) => {
    if (mode === variantViewMode) return;
    skipFilterFetchOnceRef.current = true;
    dispatch(setFilterField({ pageKey, field: "variantViewMode", value: mode }));
    setPageNumber(1);
    fetchSalesData(mode);
  };

  // Export functions - Commented out for now (incomplete implementation)
  /* 
  const exportToCSV = () => {
    const csvContent = [
      "Product Name,SKU,Quantity Sold,Gross Sales,Discounts & Promotions,Net Sales Inc. Tax,Net Sales Exc. Tax,Tax Amount,Cost of Goods,Gross Profit,Gross Profit Margin %,Transaction Qty,Refund Qty,Refund Value",
      ...salesData.map(row =>
        `${row.productName},${row.productSKU},${row.quantitySold},${row.grossSales},${row.discountsPromotions},${row.netSalesIncTax},${row.netSalesExcTax},${row.taxAmount},${row.costOfGoods},${row.grossProfit},${row.grossProfitMargin},${row.transactionQty},${row.refundQty},${row.refundValue}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales-breakdown-by-product.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    showSuccessToast("CSV file exported successfully");
  };

  const exportToWord = () => {
    // Implementation pending
  };

  const exportToExcel = () => {
    // Implementation pending
  };

  const printReport = () => {
    // Implementation pending
  };
  */

  // Fetch reporting categories (Category Types) from tbl_CategoryTypes
  const fetchReportingCategories = useCallback(async () => {
    try {
      setLoadingReportingCategories(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          CompID
        }
      });
      const categoryTypes = response.data.data || response.data || [];

      // Transform category types to dropdown format
      const categoryTypeOptions = [
        { value: "all", label: "All Category Types" },
        ...categoryTypes.map(categoryType => ({
          value: categoryType.rC_ID || categoryType.RC_ID || categoryType.id,
          label: categoryType.rC_Name || categoryType.RC_Name || categoryType.name
        }))
      ];

      setReportingCategories(categoryTypeOptions);
    } catch (error) {
      console.error('Error fetching category types:', error);
      showErrorToast("Error loading category types");
      setReportingCategories([{ value: "all", label: "All Category Types" }]);
    } finally {
      setLoadingReportingCategories(false);
    }
  }, [CompID]);

  // Fetch locations (company branches): company name + branch names only (no "All Locations")
  const fetchLocations = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";

      const base = [{ value: parentId, label: compName }];

      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (pidNum === parentId && idNum !== parentId) return true;
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);

      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, profileData?.compName, profileData?.CompName]);

  // Fetch staff (excluding riders)
  const fetchStaff = useCallback(async () => {
    try {
      setLoadingStaff(true);
      
      // Fetch staff list
      const staffResponse = await axiosInstance.get(APIs.GET_ALL_STAFF, {
        params: {
          CompID
        }
      });
      const staffData = staffResponse.data.data || staffResponse.data || [];

      // Fetch roles to identify riders
      let riderRoleIds = new Set();
      try {
        const rolesResponse = await axiosInstance.get(APIs.GET_ROLES, {
          params: {
            CompID
          }
        });
        const rolesData = rolesResponse.data.data || rolesResponse.data || [];
        // Find all role IDs with "Rider" title (case-insensitive)
        rolesData.forEach(role => {
          if (role.r_Title && role.r_Title.toLowerCase() === 'rider') {
            riderRoleIds.add(role.comRolePermission_ID);
          }
        });
      } catch (roleError) {
        console.error('Error fetching roles:', roleError);
        // Continue without role filtering if roles can't be fetched
      }

      // Filter out staff with rider role
      const filteredStaff = staffData.filter(staffMember => {
        // Exclude if staff has a rider role
        const roleId = staffMember.comRolePermission_ID || staffMember.ComRolePermission_ID || staffMember.userRoles?.comRolePermission_ID;
        return !riderRoleIds.has(roleId);
      });

      // Transform staff to dropdown format
      const staffOptions = [
        { value: "all", label: "All Staff" },
        ...filteredStaff.map(staffMember => ({
          value: staffMember.userName,
          label: `${staffMember.firstName || staffMember.FirstName || ''} ${staffMember.lastName || staffMember.LastName || ''}`.trim()  
        }))
      ];

      setStaffData(staffOptions);
    } catch (error) {
      console.error('Error fetching staff:', error);
      showErrorToast(error.response?.data?.message || "Error loading staff");
    } finally {
      setLoadingStaff(false);
    }
  }, [CompID]);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    try {
      setLoadingDevices(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DEVICES, {
        params: {
          CompID
        }
      });
      const deviceData = response.data.data || response.data || [];

      // Transform devices to dropdown format
      const deviceOptions = [
        { value: "all", label: "All Devices" },
        ...deviceData.map(device => ({
          value: device.deviceID,
          label: device.deviceName
        }))
      ];

      setDevices(deviceOptions);
    } catch (error) {
      console.error('Error fetching devices:', error);
      showErrorToast(error.response?.data?.message || "Error loading devices");
    } finally {
      setLoadingDevices(false);
    }
  }, [CompID]);

  // Transform hierarchical categories to flat list with parent-child indication
  const transformCategoriesToOptions = (categories) => {
    const options = [];

    categories.forEach(category => {
      // Add parent category
      options.push({
        value: category.rootCat_ID,
        label: category.rootCat_Name
      });

      // Add subcategories with indentation
      if (category.subCategoriesList && category.subCategoriesList.length > 0) {
        category.subCategoriesList.forEach(subCategory => {
          options.push({
            value: subCategory.rootCat_ID,
            label: `  ↳ ${subCategory.rootCat_Name}`
          });
        });
      }
    });

    return options;
  };

  // Fetch categories (Root Categories with subcategories)
  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const response = await axiosInstance.get(APIs.GET_ALL_REPORTING_CATEGORIES, {
        params: {
          CompID
        }
      });

      if (response.data.status && response.data.data) {
        const categoryData = response.data.data;
        
        // Transform reporting categories to dropdown format
        const categoryOptions = [
          { value: "all", label: "All Reporting Categories" },
          ...categoryData.map(category => ({
            value: category.rootCat_ID,
            label: category.rootCat_Name
          }))
        ];

        setCategories(categoryOptions);
      } else {
        setCategories([{ value: "all", label: "All Reporting Categories" }]);
      }
    } catch (error) {
      console.error('Error fetching reporting categories:', error);
      console.error('Reporting Category API Error Details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      showErrorToast(error.response?.data?.message || error.message || "Error loading reporting categories");
      setCategories([{ value: "all", label: "All Reporting Categories" }]);
    } finally {
      setLoadingCategories(false);
    }
  }, [CompID]);

  // Fetch menus (changed from menu groups)
  const fetchMenuGroups = useCallback(async () => {
    try {
      setLoadingMenuGroups(true);
      const response = await axiosInstance.get(APIs.GET_MENU_LIST, {
        params: {
          CompID,
          SortBy: 'Menu_ID',
          SortOrder: 'ASC',
          Search: ''
        }
      });
      const menuData = response.data.data || response.data || [];

      // Transform menus to dropdown format
      const menuOptions = [
        { value: "all", label: "All Menus" },
        ...(Array.isArray(menuData) ? menuData.map(menu => ({
          value: menu.menu_ID || menu.Menu_ID || menu.id,
          label: menu.menu_Name || menu.Menu_Name || menu.name
        })) : [])
      ];

      setMenuGroups(menuOptions);
    } catch (error) {
      console.error('Error fetching menus:', error);
      showErrorToast(error.response?.data?.message || "Error loading menus");
      setMenuGroups([{ value: "all", label: "All Menus" }]);
    } finally {
      setLoadingMenuGroups(false);
    }
  }, [CompID]);

  // Handle date range changes (preserves date + time from picker when using Custom Date with time)
  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, null];
    // Picker clear → restore default "This Week" (same as Clear filters)
    if (
      (!newStartDate || !moment(newStartDate).isValid()) &&
      (!newEndDate || !moment(newEndDate).isValid())
    ) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
      setPageNumber(1);
      return;
    }
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      let effectiveStartDate = newStartDate;
      let effectiveEndDate = newEndDate;
      if (newStartDate && newEndDate && newStartDate > newEndDate) {
        [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
      } else if (newStartDate && !newEndDate) {
        effectiveEndDate = new Date(newStartDate.getTime());
        effectiveEndDate.setHours(23, 59, 59, 999);
      }
      dispatch(
        setFilterField({
          pageKey,
          field: "dateRange",
          value: [effectiveStartDate, effectiveEndDate],
        })
      );
      setPageNumber(1);
    } else {
      showErrorToast("Invalid date selected");
    }
  };

  // Refresh data
  const refreshData = () => {
    fetchSalesData();
  };

  // Initialize preferences
  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    
    // Initialize filter fields
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
    if (!preferences.staff) {
      dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    }
    if (!preferences.device) {
      dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    }
    if (!preferences.reportingCategory) {
      dispatch(setFilterField({ pageKey, field: "reportingCategory", value: defaultReportingCategory }));
    } else if (preferences.reportingCategory.label === "All Reporting Categories") {
      // Migrate old label to new label
      dispatch(setFilterField({ pageKey, field: "reportingCategory", value: defaultReportingCategory }));
    }
    if (!preferences.category) {
      dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
    }
    if (!preferences.menuGroup) {
      dispatch(setFilterField({ pageKey, field: "menuGroup", value: defaultMenuGroup }));
    }
    if (!preferences.productType) {
      dispatch(setFilterField({ pageKey, field: "productType", value: defaultProductType }));
    } else {
      const savedValue = preferences.productType?.value;
      // Migrate old "Products" type filter to Selling Products
      if (savedValue === "PRODUCT") {
        dispatch(setFilterField({
          pageKey,
          field: "productType",
          value: { value: "SellingItems", label: "Selling Products" },
        }));
      } else if (savedValue && !["all", "SellingItems", "NonSellingItems", "MODIFIER", "COMBO"].includes(savedValue)) {
        dispatch(setFilterField({ pageKey, field: "productType", value: defaultProductType }));
      }
    }
    if (!preferences.dateRange) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    }
    if (!preferences.variantViewMode) {
      dispatch(setFilterField({ pageKey, field: "variantViewMode", value: defaultVariantViewMode }));
    }
  }, [dispatch, pageKey]);

  // Track if component has mounted and if initial preferences are loaded
  const [isInitialized, setIsInitialized] = useState(false);
  const isFirstRender = useRef(true);
  const hasInitialFetch = useRef(false);

  // Load initial data only once after preferences are initialized
  useEffect(() => {
    // Check if all required preferences are initialized
    if (preferences.visibleColumns && preferences.sortOrder && preferences.sortBy && 
        preferences.location && preferences.staff && preferences.device && 
        preferences.reportingCategory && preferences.category && preferences.menuGroup && 
        preferences.productType && preferences.dateRange) {
      if (!isInitialized) {
        setIsInitialized(true);
        hasInitialFetch.current = true;
        skipFilterFetchOnceRef.current = true;
        fetchSalesData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, isInitialized]);

  // Watch for filter changes and apply filters (but not on initial mount)
  useEffect(() => {
    if (!isInitialized || isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (skipFilterFetchOnceRef.current) {
      skipFilterFetchOnceRef.current = false;
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    scheduleFetchSalesData();
    return () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, staff, device, reportingCategory, category, menuGroup, productType, rawStartDate, rawEndDate, isInitialized]);

  // Fetch all dropdown data on component mount; locations only for parent company
  useEffect(() => {
    fetchReportingCategories();
    if (isMain) fetchLocations();
    fetchStaff();
    fetchDevices();
    fetchCategories();
    fetchMenuGroups();
  }, [isMain, fetchReportingCategories, fetchLocations, fetchStaff, fetchDevices, fetchCategories, fetchMenuGroups]);

  // When user/account changes (login, logout+login as different account), reset location and refetch so data loads without refresh
  useEffect(() => {
    if (!CompID) return;
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, isMain, dispatch, pageKey]);

  // Cleanup timers and in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }
      fetchAbortRef.current?.abort();
    };
  }, []);

  // Track pagination effect first run
  const paginationFirstRun = useRef(true);
  
  // Watch for pagination / sort changes (but not on initial fetch)
  useEffect(() => {
    if (paginationFirstRun.current) {
      paginationFirstRun.current = false;
      return;
    }
    if (isInitialized) {
      scheduleFetchSalesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, sortBy, sortOrder, isInitialized]);

  // Migrate old reportingCategory label if needed
  useEffect(() => {
    if (reportingCategory && 
        (reportingCategory.label === "All Reporting Categories" || 
         reportingCategory.label?.includes("Reporting Categor"))) {
      dispatch(setFilterField({ 
        pageKey, 
        field: "reportingCategory", 
        value: { value: "all", label: "All Category Types" }
      }));
    }
  }, [reportingCategory, dispatch, pageKey]);

  return (
    <div>
      {loading && <Preloader />}

      {/* Breadcrumb */}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Sales by Products</li>
          </ol>
          <h1 className="page-header mb-0">Sales by Products</h1>
        </div>
      </div>

      {/* Filter Bar - Top Section */}
      <div 
        className="card mb-4"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: '12px',
          border: 'none',
          boxShadow: `0 4px 20px ${themeColor}40`
        }}
      >
        <div className="card-body p-3">

          {/* Date Range and Search Section */}
          <div className="row g-3 align-items-center mb-3">

            <div className="col-xl-2 col-lg-3 col-md-6">
              <div className="filter-control">
                <div
                  style={{
                    position: 'relative',
                    zIndex: 9999,
                    width: '100%'
                  }}
                >
                  <DateRangePicker
                    startDate={rawStartDate ? (typeof rawStartDate === 'string' ? new Date(rawStartDate) : rawStartDate) : null}
                    endDate={rawEndDate ? (typeof rawEndDate === 'string' ? new Date(rawEndDate) : rawEndDate) : null}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                    className="filter-dropdown"
                    allowSingleDate={true}
                    allowDateRange={true}
                    showTimeSelection={true}
                  />
                </div>
              </div>
            </div>

            {isMain && locationOptions.length > 1 && (
              <div className="col-xl-2 col-lg-2 col-md-6">
                <div className="filter-control">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange('location', option)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    closeMenuOnScroll={false}
                  />
                </div>
              </div>
            )}

            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={staff}
                  onChange={(option) => handleFilterChange('staff', option)}
                  options={staffOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingStaff}
                  placeholder="Staff"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>

            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="filter-control d-flex align-items-center h-100">
                <div className="variant-view-toggle" role="group" aria-label="Product variant view">
                  <button
                    type="button"
                    className={variantViewMode === "variant" ? "active" : ""}
                    onClick={() => handleVariantViewModeChange("variant")}
                  >
                    Variant-wise
                  </button>
                  <button
                    type="button"
                    className={variantViewMode === "combined" ? "active" : ""}
                    onClick={() => handleVariantViewModeChange("combined")}
                  >
                    Combined Variants
                  </button>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="search-input filter-control">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by name, type, category..."
                  value={search}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#333'
                  }}
                />
              </div>
            </div>

            <div className="col-xl-2 col-lg-3 col-md-12">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="clear-btn"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  <i className="fa fa-times me-1"></i>
                  Filter
                </button>

                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                />

                {/* Export buttons - Commented out for now (incomplete implementation) */}
                {/* 
                <button
                  className="btn btn-sm"
                  onClick={exportToCSV}
                  style={{
                    height: "32px",
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    borderRadius: '6px'
                  }}
                >
                  <i className="fa fa-file-csv me-1"></i>
                  CSV
                </button>

                <button
                  className="btn btn-sm"
                  onClick={exportToWord}
                  style={{
                    height: "32px",
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    borderRadius: '6px'
                  }}
                >
                  <i className="fa fa-file-word me-1"></i>
                  Word
                </button>

                <button
                  className="btn btn-sm"
                  onClick={exportToExcel}
                  style={{
                    height: "32px",
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    borderRadius: '6px'
                  }}
                >
                  <i className="fa fa-file-excel me-1"></i>
                  Excel
                </button>

                <button
                  className="btn btn-sm"
                  onClick={printReport}
                  style={{
                    height: "32px",
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    borderRadius: '6px'
                  }}
                >
                  <i className="fa fa-print me-1"></i>
                  Print
                </button>
                */}
              </div>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="row g-3 align-items-center">
           
           
            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={device}
                  onChange={(option) => handleFilterChange('device', option)}
                  options={deviceOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingDevices}
                  placeholder="Device"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={reportingCategory}
                  onChange={(option) => handleFilterChange('reportingCategory', option)}
                  options={reportingCategoryOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingReportingCategories}
                  placeholder="Category Type"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={category}
                  onChange={(option) => handleFilterChange('category', option)}
                  options={categoryOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingCategories}
                  placeholder="Reporting Category"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-2 col-md-6 col-sm-6">
              <div className="filter-control">
                <Select
                  value={menuGroup}
                  onChange={(option) => handleFilterChange('menuGroup', option)}
                  options={menuGroupOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingMenuGroups}
                  placeholder="Menu"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-2 col-md-6 col-sm-6">
              <div className="filter-control">
                <Select
                  value={productType}
                  onChange={(option) => handleFilterChange('productType', option)}
                  options={productTypeOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder="Product Type"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Data Table */}
      <div className="card" style={{ position: 'relative', zIndex: 1 }}>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead style={{ position: 'sticky', top: 0, zIndex: 5, backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa' }}>
                <tr>
                  <th>PRODUCT NAME</th>
                  {visibleColumns.productSKU && <th>SKU</th>}
                  {visibleColumns.quantitySold && <th>QTY SOLD</th>}
                  {visibleColumns.grossSales && <th>GROSS SALES</th>}
                  {visibleColumns.discountsPromotions && <th>DISCOUNTS</th>}
                  {visibleColumns.netSalesIncTax && <th>NET SALES INC. TAX</th>}
                  {visibleColumns.netSalesExcTax && <th>NET SALES EXC. TAX</th>}
                  {visibleColumns.taxAmount && <th>TAX AMOUNT</th>}
                  {visibleColumns.costOfGoods && <th>COST OF GOODS</th>}
                  {visibleColumns.grossProfit && <th>GROSS PROFIT</th>}
                  {visibleColumns.grossProfitMargin && <th>GROSS PROFIT MARGIN %</th>}
                  {visibleColumns.transactionQty && <th>TRANSACTION QTY</th>}
                  {visibleColumns.refundQty && <th>REFUND QTY</th>}
                  {visibleColumns.refundValue && <th>REFUND VALUE</th>}
                </tr>
              </thead>
              <tbody>
                {!loading && salesData.length === 0 ? (
                  <tr>
                    <td colSpan={20} className="text-center py-4 text-muted">
                      No products match the selected filters.
                    </td>
                  </tr>
                ) : (
                salesData.map((row, index) => (
                  <tr key={`${row.menuItemID ?? index}-${row.variantName || ""}`}>
                    <td>{row.productName}</td>
                    {visibleColumns.productSKU && <td>{row.productSKU}</td>}
                    {visibleColumns.quantitySold && <td>{row.quantitySold}</td>}
                    {visibleColumns.grossSales && <td style={{ color: '#0d6efd' }}>{getCurrencySymbol(compCurrency)} {row.grossSales.toFixed(2)}</td>}
                    {visibleColumns.discountsPromotions && <td>{getCurrencySymbol(compCurrency)} {row.discountsPromotions.toFixed(2)}</td>}
                    {visibleColumns.netSalesIncTax && <td>{getCurrencySymbol(compCurrency)} {row.netSalesIncTax.toFixed(2)}</td>}
                    {visibleColumns.netSalesExcTax && <td>{getCurrencySymbol(compCurrency)} {row.netSalesExcTax.toFixed(2)}</td>}
                    {visibleColumns.taxAmount && <td>{getCurrencySymbol(compCurrency)} {row.taxAmount.toFixed(2)}</td>}
                    {visibleColumns.costOfGoods && <td>{getCurrencySymbol(compCurrency)} {row.costOfGoods.toFixed(2)}</td>}
                    {visibleColumns.grossProfit && <td style={{ color: row.grossProfit >= 0 ? '#198754' : '#dc3545' }}>{getCurrencySymbol(compCurrency)} {row.grossProfit.toFixed(2)}</td>}
                    {visibleColumns.grossProfitMargin && <td style={{ color: row.grossProfitMargin >= 0 ? '#198754' : '#dc3545' }}>{row.grossProfitMargin.toFixed(2)}%</td>}
                    {visibleColumns.transactionQty && <td>{row.transactionQty}</td>}
                    {visibleColumns.refundQty && <td>{row.refundQty}</td>}
                    {visibleColumns.refundValue && <td>{getCurrencySymbol(compCurrency)} {row.refundValue.toFixed(2)}</td>}
                  </tr>
                )))}
              </tbody>
            </table>
          </div>

          {/* Pagination Component */}
          <Pagination
            totalRecords={totalRecords}
            pageSize={pageSize}
            pageNumber={pageNumber}
            totalPages={totalPages}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            themeColor={themeColor}
          />
        </div>
      </div>
    </div>
  );
};

export default SalesByProducts;

